import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import CaseCard from "@/components/feature/CaseCard";
import ArticleBody from "@/pages/cases/detail/components/ArticleBody";
import ArticleAside from "@/pages/cases/detail/components/ArticleAside";
import { usePageMeta, SITE_ORIGIN } from "@/hooks/usePageMeta";
import { caseArticles } from "@/mocks/cases";
import { siteInfo } from "@/mocks/site";

export default function CaseDetailPage() {
  const { slug } = useParams();
  const article = caseArticles.find((item) => item.slug === slug);

  const headings = article
    ? article.content
        .map((block, index) => ({ block, id: `heading-${index}` }))
        .filter((item) => item.block.type === "h2" && Boolean(item.block.text))
        .map((item) => ({ id: item.id, text: item.block.text || "" }))
    : [];

  const related = article
    ? caseArticles
        .filter((item) => item.slug !== article.slug && item.category === article.category)
        .concat(
          caseArticles.filter(
            (item) => item.slug !== article.slug && item.category !== article.category
          )
        )
        .slice(0, 3)
    : [];

  const jsonLd = article
    ? {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "BlogPosting",
            headline: article.title,
            description: article.excerpt,
            image: article.cover,
            datePublished: article.date,
            dateModified: article.date,
            author: { "@type": "Organization", name: article.author },
            publisher: { "@type": "Organization", name: siteInfo.name },
            mainEntityOfPage: `${SITE_ORIGIN}/cases/${article.slug}`,
            inLanguage: "zh-CN",
            keywords: article.tags.join(","),
            articleSection: article.category,
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
              {
                "@type": "ListItem",
                position: 2,
                name: "调查档案",
                item: `${SITE_ORIGIN}/cases`,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `${SITE_ORIGIN}/cases/${article.slug}`,
              },
            ],
          },
        ],
      }
    : undefined;

  usePageMeta({
    title: article ? `${article.title} - ${siteInfo.name}` : `档案未找到 - ${siteInfo.name}`,
    description: article
      ? article.excerpt
      : "该调查档案不存在或已被移除，你可以返回调查档案列表继续浏览。",
    keywords: article ? article.tags.join(",") : "网暴黑料,调查档案",
    path: `/cases/${slug || ""}`,
    type: "article",
    jsonLd,
  });

  if (!article) {
    return (
      <Layout>
        <section className="w-full bg-background-50 py-24">
          <div className="max-w-content mx-auto px-4 md:px-6 text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-400" aria-hidden="true"></i>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
              没有找到这份档案
            </h1>
            <p className="mt-3 text-sm text-foreground-600">
              它可能已经更名或被移除，去档案列表看看其他内容吧。
            </p>
            <Link
              to="/cases"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line" aria-hidden="true"></i>
              返回调查档案
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <article>
        <header className="w-full bg-background-100 border-b border-background-200">
          <div className="max-w-content mx-auto px-4 md:px-6 py-8 md:py-12">
            <Breadcrumbs
              items={[
                { label: "首页", to: "/" },
                { label: "调查档案", to: "/cases" },
                { label: article.category, to: `/cases?category=${encodeURIComponent(article.category)}` },
                { label: article.title },
              ]}
            />
            <div className="mt-6 md:mt-8 max-w-4xl">
              <div className="flex flex-wrap items-center gap-2.5">
                <Link
                  to={`/cases?category=${encodeURIComponent(article.category)}`}
                  className="text-xs font-label px-3 py-1 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  {article.category}
                </Link>
                <span className="text-xs text-foreground-500">{article.readTime}阅读</span>
              </div>
              <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-snug">
                {article.title}
              </h1>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                {article.excerpt}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs md:text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-quill-pen-line" aria-hidden="true"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" aria-hidden="true"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-price-tag-3-line" aria-hidden="true"></i>
                  {article.tags.slice(0, 2).join(" / ")}
                </span>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-content mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="w-full h-56 md:h-[420px] rounded-lg overflow-hidden bg-background-200">
            <img
              src={article.cover}
              alt={`${article.title} 配图`}
              title={`${article.title} · 网暴黑料调查`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="mt-10 md:mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-8 min-w-0">
              <p className="p-5 rounded-md bg-secondary-50 border-l-4 border-secondary-500 font-heading text-base md:text-lg font-semibold text-secondary-950 leading-relaxed">
                {article.intro}
              </p>
              <div className="mt-8">
                <ArticleBody blocks={article.content} />
              </div>

              <div className="mt-10 pt-6 border-t border-background-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-foreground-500 leading-relaxed max-w-xl">
                  本文基于公开信息与一般性规律整理，仅用于公共议题讨论，不针对任何具体个人作出事实判断，亦不构成法律意见。
                </p>
                <Link
                  to="/cases"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer bg-background-100 border border-background-300 text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors shrink-0"
                >
                  <i className="ri-arrow-left-line" aria-hidden="true"></i>
                  返回档案列表
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4">
              <ArticleAside headings={headings} tags={article.tags} related={related} />
            </div>
          </div>
        </div>

        <section className="w-full bg-background-100 border-t border-background-200 py-12 md:py-16">
          <div className="max-w-content mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between gap-4">
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-950">
                继续往下读
              </h2>
              <Link
                to="/cases"
                className="text-sm text-foreground-600 hover:text-primary-600 whitespace-nowrap transition-colors cursor-pointer"
              >
                全部档案
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {related.map((item) => (
                <CaseCard key={item.slug} article={item} />
              ))}
            </div>
          </div>
        </section>
      </article>
    </Layout>
  );
}